import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, ArrowRight, Package, Truck, BadgeCheck, Globe } from 'lucide-react';
import { Button } from './ui/button';

const PartsSection = () => {
  return (
    <section id="parts" data-testid="parts-section" className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium">
              <Globe className="w-4 h-4" />
              Ships Nationwide
            </span>
            
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">
              Buy Appliance Parts Online
            </h2>
            
            <p className="text-lg text-slate-300">
              We ship appliance parts to all 50 states! Submit a parts request 
              and our team will locate the exact part you need at competitive prices.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Nationwide Shipping</h4>
                  <p className="text-sm text-slate-400">Ships to all 50 US states</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-white">OEM & Aftermarket</h4>
                  <p className="text-sm text-slate-400">Quality parts for all brands</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/parts">
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="parts-section-cta"
                >
                  <Wrench className="w-5 h-5 mr-2" />
                  Request a Part
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:+18556056034">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  Call (855) 605-6034
                </Button>
              </a>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl p-8 border border-slate-700">
              <h3 className="text-xl font-bold mb-6 text-white">How It Works</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Submit Your Request</h4>
                    <p className="text-sm text-slate-400">Tell us your appliance type, model, and part details</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-white">We Find the Part</h4>
                    <p className="text-sm text-slate-400">Our team searches our network for the best match</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Get Your Quote</h4>
                    <p className="text-sm text-slate-400">We contact you with availability and pricing</p>
                  </div>
                </div>
              </div>

              {/* Appliance Icons */}
              <div className="mt-8 pt-6 border-t border-slate-600">
                <p className="text-sm text-slate-400 mb-3">Parts for all major appliances:</p>
                <div className="flex flex-wrap gap-2">
                  {['Washers', 'Dryers', 'Refrigerators', 'Ovens', 'Dishwashers'].map((item) => (
                    <span 
                      key={item}
                      className="px-3 py-1 bg-slate-600 rounded-full text-xs text-slate-300"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartsSection;
